import type { Action } from 'redux'
import type { StreamDefinition } from '../../signalr/types'
import { defaultGetParams as getParams } from '../../signalr/helpers'
import {
  addCancelOrderAlerts,
  addCounteredToppedAlerts,
  addNewOrderAlerts,
  addTradingNowAlerts
} from './actions'
import {
  cancelOrderAlertFromResponse,
  counteredToppedAlertFromResponse,
  newOrderAlertFromResponse,
  tradingNowAlertFromResponse
} from './helpers'
import type { State } from './reducer'
import {
  getMutedCounteredAlertsSecurityIds,
  getMutedNewOrderAlertsSecurityIds,
  getMutedToppedAlertsSecurityIds,
  getMutedTradingNowAlertsSecurityIds
} from './selectors'

const ALERT_CANCEL_ON = ['alerts.unsubscribe']
const ALERT_BUFFER_MS = 300

type AlertsRoot = { alerts: State }
type RawAlertResponse = { securityId: number }
type RawCounteredToppedResponse = RawAlertResponse & { alertType: number }

export const subscribeTradingNowAlertsDefinition: StreamDefinition<
  [],
  RawAlertResponse[],
  Action,
  AlertsRoot
> = {
  method: 'GetTradingNowAlerts',
  strategy: 'merge',
  getParams,
  filterResponse: (raw, state) =>
    !getMutedTradingNowAlertsSecurityIds(state).includes(raw.securityId),
  mapResponse: (batch) =>
    addTradingNowAlerts(batch.map(tradingNowAlertFromResponse)),
  cancelOn: ALERT_CANCEL_ON,
  bufferMs: ALERT_BUFFER_MS
}

export const subscribeNewOrderAlertsDefinition: StreamDefinition<
  [],
  RawAlertResponse[],
  Action,
  AlertsRoot
> = {
  method: 'GetNewOrderAlerts',
  strategy: 'merge',
  getParams,
  filterResponse: (raw, state) =>
    !getMutedNewOrderAlertsSecurityIds(state).includes(raw.securityId),
  mapResponse: (batch) =>
    addNewOrderAlerts(batch.map(newOrderAlertFromResponse)),
  cancelOn: ALERT_CANCEL_ON,
  bufferMs: ALERT_BUFFER_MS
}

export const subscribeCounteredToppedAlertsDefinition: StreamDefinition<
  [],
  RawCounteredToppedResponse[],
  Action,
  AlertsRoot
> = {
  method: 'GetCounteredToppedAlerts',
  strategy: 'merge',
  getParams,
  /* alertType is 0 (countered) or 1 (topped) on the raw hub response */
  filterResponse: (raw, state) => {
    const securityIds =
      raw.alertType === 0
        ? getMutedCounteredAlertsSecurityIds(state)
        : getMutedToppedAlertsSecurityIds(state)
    return !securityIds.includes(raw.securityId)
  },
  mapResponse: (batch) =>
    addCounteredToppedAlerts(batch.map(counteredToppedAlertFromResponse)),
  cancelOn: ALERT_CANCEL_ON,
  bufferMs: ALERT_BUFFER_MS
}

export const subscribeCancelOrderAlertsDefinition: StreamDefinition<
  [],
  RawAlertResponse[],
  Action
> = {
  method: 'GetOrderCancelAlerts',
  strategy: 'merge',
  getParams,
  mapResponse: (batch) =>
    addCancelOrderAlerts(batch.map(cancelOrderAlertFromResponse)),
  cancelOn: ALERT_CANCEL_ON,
  bufferMs: ALERT_BUFFER_MS
}
